import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarIcon, ArrowIcon } from '@/components/icons';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/format';
import { toast } from '@/store/useToastStore';
import {
  addDays,
  addMonths,
  buildMonthGrid,
  fromIsoDate,
  inclusiveDaySpan,
  isSameDay,
  startOfDay,
  startOfMonth,
  toIsoDate,
} from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface Preset {
  label: string;
  getRange: (today: Date) => { start: Date; end: Date };
}

const PRESETS: Preset[] = [
  { label: 'Today', getRange: (today) => ({ start: today, end: today }) },
  { label: 'Yesterday', getRange: (today) => ({ start: addDays(today, -1), end: addDays(today, -1) }) },
  { label: 'Last 7 Days', getRange: (today) => ({ start: addDays(today, -6), end: today }) },
  { label: 'This Month', getRange: (today) => ({ start: startOfMonth(today), end: today }) },
  { label: 'Last 30 Days', getRange: (today) => ({ start: addDays(today, -29), end: today }) },
];

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Maximum selectable span, inclusive. Defaults to 60 days. */
  maxRangeDays?: number;
  className?: string;
}

export function DateRangePicker({ value, onChange, maxRangeDays = 60, className }: DateRangePickerProps) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const maxLeftMonth = useMemo(() => addMonths(today, -1), [today]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(fromIsoDate(value.from)));
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);
  const [hover, setHover] = useState<Date | null>(null);

  // Seed the draft + visible months from the committed value each time we open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const start = fromIsoDate(value.from);
    const end = fromIsoDate(value.to);
    setDraftStart(start);
    setDraftEnd(end);
    setHover(null);
    setViewMonth(clampView(startOfMonth(start)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, value.from, value.to]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handlePointer = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  function clampView(candidate: Date): Date {
    return candidate > maxLeftMonth ? maxLeftMonth : candidate;
  }

  const atMaxRight = startOfMonth(viewMonth).getTime() >= maxLeftMonth.getTime();

  function commit(start: Date, end: Date) {
    onChange({ from: toIsoDate(start), to: toIsoDate(end) });
    setIsOpen(false);
  }

  function selectDay(date: Date) {
    if (date > today) {
      return;
    }
    // Fresh selection: either nothing picked yet, or a complete range is showing.
    if (!draftStart || draftEnd) {
      setDraftStart(date);
      setDraftEnd(null);
      setHover(null);
      return;
    }
    // An earlier day restarts the range from there.
    if (date < draftStart) {
      setDraftStart(date);
      return;
    }
    if (inclusiveDaySpan(draftStart, date) > maxRangeDays) {
      toast.error(`Please pick a range of ${maxRangeDays} days or less.`);
      return;
    }
    commit(draftStart, date);
  }

  function applyPreset(preset: Preset) {
    const { start, end } = preset.getRange(today);
    commit(startOfDay(start), startOfDay(end));
  }

  function isDayDisabled(date: Date): boolean {
    if (date > today) {
      return true;
    }
    if (draftStart && !draftEnd && date > draftStart && inclusiveDaySpan(draftStart, date) > maxRangeDays) {
      return true;
    }
    return false;
  }

  // Effective range to render — previews the hovered end while picking.
  const rangeStart = draftStart;
  let rangeEnd = draftEnd;
  if (
    draftStart &&
    !draftEnd &&
    hover &&
    hover >= draftStart &&
    inclusiveDaySpan(draftStart, hover) <= maxRangeDays
  ) {
    rangeEnd = hover;
  }
  const hasRange = Boolean(rangeStart && rangeEnd && !isSameDay(rangeStart, rangeEnd));

  const rightMonth = addMonths(viewMonth, 1);
  const activeEndpoint: 'start' | 'end' = draftStart && !draftEnd ? 'end' : 'start';

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm text-ink transition-colors hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-brand"
      >
        <CalendarIcon />
        <span className="font-medium">{formatDate(value.from)}</span>
        <ArrowIcon />
        <span className="font-medium">{formatDate(value.to)}</span>
      </button>

      {isOpen ? (
        <div className="absolute right-0 z-40 mt-2 w-[min(36rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-line bg-surface shadow-pop">
          {/* Selected range summary */}
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <RangeChip label="Start" value={draftStart} active={activeEndpoint === 'start'} />
            <ArrowIcon />
            <RangeChip label="End" value={draftEnd} active={activeEndpoint === 'end'} />
          </div>

          {/* Calendars */}
          <div className="px-4 pt-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex gap-1">
                <NavButton label="Previous year" onClick={() => setViewMonth(clampView(addMonths(viewMonth, -12)))}>
                  «
                </NavButton>
                <NavButton label="Previous month" onClick={() => setViewMonth(clampView(addMonths(viewMonth, -1)))}>
                  ‹
                </NavButton>
              </div>
              <div className="flex flex-1 justify-around px-2 text-sm font-semibold text-ink">
                <span>{monthLabel(viewMonth)}</span>
                <span className="hidden sm:block">{monthLabel(rightMonth)}</span>
              </div>
              <div className="flex gap-1">
                <NavButton
                  label="Next month"
                  disabled={atMaxRight}
                  onClick={() => setViewMonth(clampView(addMonths(viewMonth, 1)))}
                >
                  ›
                </NavButton>
                <NavButton
                  label="Next year"
                  disabled={atMaxRight}
                  onClick={() => setViewMonth(clampView(addMonths(viewMonth, 12)))}
                >
                  »
                </NavButton>
              </div>
            </div>

            <div className="flex gap-6 overflow-x-auto pb-4">
              <MonthGrid
                monthDate={viewMonth}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                hasRange={hasRange}
                isDayDisabled={isDayDisabled}
                onSelect={selectDay}
                onHover={setHover}
              />
              <div className="hidden sm:block">
                <MonthGrid
                  monthDate={rightMonth}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  hasRange={hasRange}
                  isDayDisabled={isDayDisabled}
                  onSelect={selectDay}
                  onHover={setHover}
                />
              </div>
            </div>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2 border-t border-line bg-surface-sunken/40 px-4 py-3">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => applyPreset(preset)}
                className="rounded-lg border border-brand/30 px-3 py-1.5 text-sm font-medium text-brand transition-colors hover:bg-brand/10"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MonthGrid({
  monthDate,
  rangeStart,
  rangeEnd,
  hasRange,
  isDayDisabled,
  onSelect,
  onHover,
}: {
  monthDate: Date;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  hasRange: boolean;
  isDayDisabled: (date: Date) => boolean;
  onSelect: (date: Date) => void;
  onHover: (date: Date | null) => void;
}) {
  const cells = buildMonthGrid(monthDate);
  return (
    <div className="w-[15.75rem] flex-shrink-0">
      <div className="mb-1 grid grid-cols-7">
        {WEEKDAYS.map((weekday) => (
          <span key={weekday} className="flex h-8 items-center justify-center text-xs font-medium text-ink-subtle">
            {weekday}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell) => {
          const isStart = Boolean(cell.inMonth && rangeStart && isSameDay(cell.date, rangeStart));
          const isEnd = Boolean(cell.inMonth && rangeEnd && isSameDay(cell.date, rangeEnd));
          const inRange = Boolean(
            cell.inMonth && rangeStart && rangeEnd && cell.date > rangeStart && cell.date < rangeEnd,
          );
          const isEndpoint = isStart || isEnd;
          const disabled = isDayDisabled(cell.date);
          return (
            <button
              key={cell.date.getTime()}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(cell.date)}
              onMouseEnter={() => onHover(cell.date)}
              className={cn(
                'flex h-9 items-center justify-center text-sm transition-colors',
                // Continuous highlight bar for the in-between + endpoint cells.
                hasRange && inRange && 'bg-brand/10',
                hasRange && isStart && 'rounded-l-full bg-brand/10',
                hasRange && isEnd && 'rounded-r-full bg-brand/10',
                disabled && 'cursor-not-allowed',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full',
                  isEndpoint && 'bg-brand font-semibold text-white',
                  !isEndpoint && !disabled && cell.inMonth && 'text-ink hover:bg-brand/10',
                  !isEndpoint && !cell.inMonth && 'text-ink-subtle/50',
                  !isEndpoint && disabled && 'text-ink-subtle/40',
                )}
              >
                {cell.date.getDate()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RangeChip({ label, value, active }: { label: string; value: Date | null; active: boolean }) {
  return (
    <div
      className={cn(
        'flex-1 rounded-md border px-3 py-1.5 text-sm',
        active ? 'border-brand text-ink ring-1 ring-brand/30' : 'border-line text-ink-muted',
      )}
    >
      {value ? toIsoDate(value) : <span className="text-ink-subtle">{label}</span>}
    </div>
  );
}

function NavButton({
  children,
  label,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-sunken disabled:cursor-not-allowed disabled:text-ink-subtle/40 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

function monthLabel(date: Date): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
}

